/**
 * LandingPage — the public front door of the UAT Tester Portal, at route `/`
 * OUTSIDE ProtectedRoute (story epic-uat-portal-001-9).
 *
 * Renders for anyone with the link — no account, no redirect to /login. The
 * guides and capture how-to must be readable the instant the link opens (design
 * anti-goal: "Coupling viewing the guides to auth").
 *
 * Shape (mirrors the approved mockup): top CTA strip → hero + 3-step journey →
 * walkthroughs → capture how-to → footer CTA.
 *
 * CTA routing decision (report/board):
 *   Filing a bug and viewing the status board BOTH require a lightweight account
 *   (design D1/D2). The `/report` and `/board` routes do not exist yet — they
 *   land in stories -10 and -11. Rather than link straight at them (which would
 *   404 until then), the "file a report" and "view the board" CTAs point at
 *   `/login` — the account is the prerequisite for both anyway, so this is both
 *   honest and 404-free. The invite CTA points at `/redeem`. Swap these to
 *   direct `/report` / `/board` links once -10/-11 land.
 *
 * Member-facing copy here is plain/clear and awaits a Menley voice-pass.
 */

import { Link } from 'react-router-dom';
import { JourneySteps } from './JourneySteps';
import { Walkthroughs } from './Walkthroughs';
import { CaptureHowTo } from './CaptureHowTo';

export function LandingPage() {
  return (
    <div data-testid="landing-page" className="min-h-screen">
      {/* Top CTA strip — lets a tester with an invite act immediately */}
      <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-3 text-sm">
        <span className="font-semibold">Hello World DAO — Tester Portal</span>
        <nav className="flex items-center gap-4">
          <Link
            to="/guide"
            data-testid="nav-guide"
            className="underline"
          >
            How to test
          </Link>
          <Link
            to="/redeem"
            data-testid="cta-redeem"
            className="underline"
          >
            Redeem an invite
          </Link>
          <Link
            to="/login"
            data-testid="cta-login"
            className="rounded bg-green-600 px-3 py-1.5 font-medium text-white"
          >
            Sign in
          </Link>
        </nav>
      </div>

      <main>
      {/* Hero */}
      <header className="mx-auto w-full max-w-4xl px-6 pb-4 pt-16 text-center">
        <h1 className="mb-4 text-4xl font-bold" data-testid="landing-hero-heading">
          You&apos;re testing the alpha. Here&apos;s how.
        </h1>
        <p className="mx-auto mb-6 max-w-2xl text-lg text-muted-foreground">
          This is your one-stop shop for testing the Hello World DAO. Read a
          walkthrough, capture what you find, and file it here — all in one
          place. No Discord needed.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <a
            href="#walkthroughs-heading"
            data-testid="cta-start"
            className="rounded bg-green-600 px-5 py-2.5 font-medium text-white"
          >
            Start a walkthrough
          </a>
          <Link
            to="/guide"
            data-testid="cta-guide"
            className="rounded border border-gray-300 px-5 py-2.5 font-medium"
          >
            Read the full guide
          </Link>
          <Link
            to="/login"
            data-testid="cta-file-report"
            className="rounded border border-gray-300 px-5 py-2.5 font-medium"
          >
            Sign in to file a report
          </Link>
          <Link
            to="/login"
            data-testid="cta-view-board"
            className="rounded border border-gray-300 px-5 py-2.5 font-medium"
          >
            Sign in to view the board
          </Link>
        </div>
      </header>

      <JourneySteps />
      <Walkthroughs />
      <CaptureHowTo />

      {/* Footer CTA */}
      <footer className="mx-auto w-full max-w-4xl px-6 py-12 text-center">
        <h2 className="mb-3 text-xl font-semibold">Found something? File it.</h2>
        <p className="mb-5 text-sm text-muted-foreground">
          Sign in with your invite to file a report and see everything else
          testers have found.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/login"
            data-testid="cta-footer-login"
            className="rounded bg-green-600 px-5 py-2.5 font-medium text-white"
          >
            Sign in
          </Link>
          <Link
            to="/redeem"
            data-testid="cta-footer-redeem"
            className="rounded border border-gray-300 px-5 py-2.5 font-medium"
          >
            Redeem an invite
          </Link>
        </div>
      </footer>
      </main>
    </div>
  );
}
